import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	deleteTrainingParticipant,
	getTrainingParticipants,
} from "../../../reducers/TrainingParticipantReducer";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	TableCell,
	TableRow,
} from "@mui/material";
import { ExpandMore, PlaylistAddCheckCircle } from "@mui/icons-material";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import CustomTable from "../../../components/CustomTable";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import { formatDateToMonthDateYear } from "../../../utils/functions";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import AddEditTrainingRecordDialog from "./AddEditTrainingRecordDialog";

const tableHeaders = [
	"Participant #",
	"Date",
	"Course",
	"Course Location",
	"Hours",
	"Category",
	"Actions",
];

const TrainingRecordTable = (props) => {
	const { allExpanded, employeeNum, theme } = props;

	const dispatch = useDispatch();
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData } = useSelector((state) => state.user);
	const { trainingParticipants } = useSelector(
		(state) => state.trainingParticipants,
	);

	const participantPermission = getAccessPermission(
		"employees",
		"Training Records",
		"any",
	);
	const canAddParticipant = participantPermission?.Create ?? true;
	const canEditParticipant = participantPermission?.Edit ?? true;
	const canDeleteParticipant = participantPermission?.Delete ?? true;

	const [expanded, setExpanded] = useState(allExpanded);
	const [selectedId, setSelectedId] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openAddDialog, setOpenAddDialog] = useState(false);
	const [selectedTrainingRecord, setSelectedTrainingRecord] = useState(null);

	useEffect(() => {
		setExpanded(allExpanded);
	}, [allExpanded]);

	const getTrainingRecordsData = () => {
		dispatch(
			getTrainingParticipants(
				0,
				-1,
				"id",
				"asc",
				{ employeeNum: { value: employeeNum } },
				userAgency,
			),
		);
	};

	useEffect(() => {
		if (employeeNum && expanded && trainingParticipants?.length === 0) {
			getTrainingRecordsData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [employeeNum, trainingParticipants, expanded, dispatch]);

	const handleDelete = useCallback(() => {
		if (selectedId) {
			dispatch(
				deleteTrainingParticipant(
					selectedId,
					userData?.user?.ptsUserID,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
		}
	}, [dispatch, selectedId, userData?.user?.ptsUserID]);

	return (
		<>
			<Accordion
				expanded={expanded}
				onChange={() => setExpanded((prev) => !prev)}
				sx={{ mb: 1 }}
			>
				<AccordionSummary
					expandIcon={<ExpandMore />}
					sx={{
						backgroundColor: theme.accordion.backgroundColor,
						color: theme.accordion.textColor,
					}}
				>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						width="100%"
					>
						<Box display="flex" alignItems="center">
							<PlaylistAddCheckCircle sx={{ mr: 1 }} />
							<span>Training Records</span>
						</Box>
						<Box sx={{ mr: 2 }}>
							{canAddParticipant && (
								<PATAddButton
									tooltipText="Add Training Record"
									onClick={(e) => {
										e.stopPropagation();
										setOpenAddDialog(true);
									}}
								/>
							)}
						</Box>
					</Box>
				</AccordionSummary>
				<AccordionDetails>
					{trainingParticipants?.length > 0 ? (
						<CustomTable
							columns={tableHeaders}
							data={trainingParticipants}
							renderRow={(participant, index) => (
								<TableRow key={index}>
									<TableCell>
										{participant?.participantNumber || "-"}
									</TableCell>
									<TableCell>
										{participant?.courseDate
											? formatDateToMonthDateYear(
													participant.courseDate,
												)
											: "-"}
									</TableCell>
									<TableCell>
										{participant?.course || "-"}
									</TableCell>
									<TableCell>
										{participant?.courseLocation || "-"}
									</TableCell>
									<TableCell>
										{participant?.hours || "-"}
									</TableCell>
									<TableCell>
										{participant?.category || "-"}
									</TableCell>
									<TableCell>
										<Box display="flex" gap={1}>
											{canEditParticipant && (
												<PATEditButton
													tooltipText="Edit Record"
													onClick={() => {
														setSelectedTrainingRecord(
															participant,
														);
														setOpenAddDialog(true);
													}}
													size="20px"
												/>
											)}
											{canDeleteParticipant && (
												<PATDeleteButton
													tooltipText="Delete Record"
													onClick={() => {
														setSelectedId(
															participant?.id,
														);
														setOpenDeleteDialog(
															true,
														);
													}}
													size="20px"
												/>
											)}
										</Box>
									</TableCell>
								</TableRow>
							)}
						/>
					) : (
						<span>No training records found.</span>
					)}
				</AccordionDetails>
			</Accordion>

			<AddEditTrainingRecordDialog
				open={openAddDialog}
				onClose={() => {
					setOpenAddDialog(false);
					setSelectedTrainingRecord(null);
				}}
				onSave={() => getTrainingRecordsData()}
				employeeNum={employeeNum}
				initialData={selectedTrainingRecord}
			/>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedId(null);
				}}
				onConfirm={handleDelete}
				title="Delete Training Record"
				message="Are you sure you want to delete this training record?"
			/>
		</>
	);
};

export default TrainingRecordTable;
